import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { hero as defaultHero } from '../data/mockData';
import AnimatedHeroVisual from './AnimatedHeroVisual';

const FADE_UP = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };

const HeroBackground = () => (
  <>
    <div className="absolute inset-0 hero-mesh" />
    <div className="absolute inset-0 noise-overlay" />
    <motion.div
      className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#FF6B35]/15 to-transparent blur-3xl"
      animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
      transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute -bottom-40 -left-40 w-[700px] h-[700px] rounded-full bg-gradient-to-tr from-blue-600/15 to-transparent blur-3xl"
      animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
      transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
    />
    <div
      className="absolute inset-0 opacity-[0.04]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }}
    />
  </>
);

const HeroSection = () => {
  const { get } = useCMS();
  const data = get('hero') || defaultHero;
  const primaryLabel = data.primaryCta?.label || data.primaryCtaLabel || 'Book a Consultation';
  const primaryHref = data.primaryCta?.href || data.primaryCtaHref || '/contact';
  const secondaryLabel = data.secondaryCta?.label || data.secondaryCtaLabel || 'View Our Work';
  const secondaryHref = data.secondaryCta?.href || data.secondaryCtaHref || '/success-stories';

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#050914]" data-testid="hero-section">
      <HeroBackground />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-8 pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.p {...FADE_UP} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-white/50 font-bold mb-8"
              data-testid="hero-eyebrow">
              <span className="w-8 h-px bg-white/40" />
              {data.eyebrow || 'DIGITAL AGENCY'}
            </motion.p>
            <motion.h1 {...FADE_UP} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-[38px] sm:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tighter leading-[1.04] text-white"
              data-testid="hero-headline">
              {data.headline}
            </motion.h1>
            <motion.p {...FADE_UP} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-7 max-w-xl text-base md:text-lg text-white/60 leading-relaxed font-light"
              data-testid="hero-subheadline">
              {data.subheadline}
            </motion.p>
            <motion.div {...FADE_UP} transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to={primaryHref}
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#050914] font-semibold hover:scale-[1.03] active:scale-[0.97] transition-transform duration-200"
                data-testid="hero-primary-cta">
                {primaryLabel}
                <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
              </Link>
              {/^https?:\/\//.test(secondaryHref) ? (
                <a href={secondaryHref} target="_blank" rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors duration-200"
                  data-testid="hero-secondary-cta">
                  {secondaryLabel}
                  <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
                </a>
              ) : (
                <Link to={secondaryHref}
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors duration-200"
                  data-testid="hero-secondary-cta">
                  {secondaryLabel}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              )}
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.3 }}
            className="lg:col-span-5">
            <AnimatedHeroVisual />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
